import { Button, Input, message, Table } from 'antd'
import { ColumnsType, SorterResult, SortOrder } from 'antd/es/table/interface'
import { Breakpoint } from 'antd/lib/_util/responsiveObserver'
import React, { FunctionComponent, useCallback, useMemo, useState } from 'react'

import { MonitoringInputType } from '../../../common/cuzkMonitorTypes'
import { monitoringInputLimit } from '../../utils/layoutConst'
import { CeeMonitorAddSubjectModal } from '../CeeMonitorAddSubjectModal/CeeMonitorAddSubjectModal'
import { CuzkMonitorAddSubjectModal } from '../CuzkMonitorAddSubjectModal/CuzkMonitorAddSubjectModal'
import { RcTableItem } from '../CuzkMonitorRcTable/CuzkMonitorRcTable'
import { Fade } from '../Fade/Fade'
import { IcoInputError } from '../IcoInputError/IcoInputError'
import { IcoNumberData } from '../IcoNumberData/IcoNumberData'
import { TabbleCellEditNoteModal } from '../TabbleCellEditNoteModal/TabbleCellEditNoteModal'

export interface RcTableItemConcept
  extends Omit<RcTableItem, 'createdAt' | 'labels'> {
  key: number
}

export interface IcoInputProps {
  type: MonitoringInputType
  buttonRef: React.MutableRefObject<null>
  inputRef: React.MutableRefObject<null>
  monitoring?: boolean
}

export const IcoInput: FunctionComponent<IcoInputProps> = props => {
  const [inputValue, setInputValue] = useState('')
  const [, setSortedInfo] = useState<SorterResult<RcTableItemConcept>>({})

  const [dataSource, setDataSource] = useState<RcTableItemConcept[]>([])

  const deleteItem = useCallback(
    (key: number) => {
      setDataSource(dataSource.filter(obj => obj.key !== key))
      message.success('Položka byla odebrána.')
    },
    [dataSource, setDataSource]
  )

  const editNote = useCallback(
    (id: number, note?: string) => {
      setDataSource(prevDataSource => {
        const index = prevDataSource.findIndex(item => item.key === id)
        if (index === -1) return prevDataSource
        const newDataSource = [...prevDataSource]
        newDataSource[index] = { ...newDataSource[index], description: note }
        return newDataSource
      })
    },
    [setDataSource]
  )

  const columns = useMemo<ColumnsType<RcTableItemConcept>>(
    () => [
      {
        title: 'ID',
        dataIndex: 'key',
        key: 'key',
        sorter: {
          compare: (a: { key: number }, b: { key: number }) => a.key - b.key,
          multiple: 1,
        },
        defaultSortOrder: 'descend' as SortOrder,
      },
      {
        title: 'Poznámka',
        dataIndex: 'description',
        key: 'description',
        render: (text: string, record) => (
          <TabbleCellEditNoteModal
            onSave={editNote}
            initialNote={text}
            recordId={record.key}
          />
        ),
      },
      {
        title: 'Zadané údaje',
        dataIndex: 'typed',
        key: 'typed',
      },
      {
        title: 'Převedeno do tvaru',
        dataIndex: 'rc',
        key: 'rc',
      },
      {
        title: 'Kód validace',
        dataIndex: 'validatedCode',
        key: 'validatedCode',
        responsive: ['' as Breakpoint],
      },
      {
        title: 'Validace IČO',
        dataIndex: 'validated',
        key: 'validated',
        sorter: {
          compare: (
            a: { validatedCode?: number },
            b: { validatedCode?: number }
          ) => (a.validatedCode || 0) - (b.validatedCode || 0),
          multiple: 2,
        },
        defaultSortOrder: 'ascend' as SortOrder,
        render: (
          _: string,
          record: { validatedCode?: number; rc?: string }
        ) => {
          return record.validatedCode === 30 ? (
            <IcoNumberData icon rcNumber={record.rc || '0'} /> // TODO
          ) : (
            <IcoInputError error={record.validatedCode || 30} />
          )
        },
      },
      {
        title: 'Akce',
        dataIndex: 'actions',
        key: 'actions',
        render: (_: string, record) => (
          <>
            <Button type="link" danger onClick={() => deleteItem(record.key)}>
              Odebrat
            </Button>
          </>
        ),
      },
    ],
    [deleteItem, editNote]
  )

  const verifyIco = useCallback((ico: string): number => {
    ico = ico.replace(/\s+/g, '')
    if (!/^\d{8}$/.test(ico)) {
      return 2
    }
    let checksum = 0
    for (let i = 0; i < 7; i++) {
      checksum += parseInt(ico[i], 10) * (8 - i)
    }
    checksum = checksum % 11
    let controlDigit
    if (checksum === 0) {
      controlDigit = 1
    } else if (checksum === 1) {
      controlDigit = 0
    } else {
      controlDigit = 11 - checksum
    }
    return parseInt(ico[7], 10) === controlDigit ? 30 : 3
  }, [])

  const handleChange = useCallback(
    (event: { target: { value: string } }) => {
      const value = event.target.value

      setInputValue(value)
      if (/[,;\n]+|\d{8}$/.test(value)) {
        const lines = value
          .replace(/[,;"']+/g, '\n')
          .split(/[\n]+/)
          .filter((line: string) => line !== '')
          .map((line: string) => line.trim())

        const newDataSource = [...dataSource]
        lines.forEach((value: string) => {
          let notice = ''
          let validatedCode = 30 // Defaultní kód validace
          let line = value
          if (value.includes(':')) {
            const split = value.split(':')
            line = split[1]
            notice = split[0]
          }

          line = line.replace(/[/ \s]+/g, '')
          validatedCode = verifyIco(line)

          const isDuplicate = newDataSource.some(row => row.ico === line)
          validatedCode = isDuplicate ? 5 : validatedCode

          newDataSource.push({
            id: value,
            key: newDataSource.length
              ? (newDataSource[newDataSource.length - 1]?.key || 0) + 1
              : 1,
            ico: line,
            description: notice,
            typed: value,
            validatedCode: validatedCode,
          })

          setInputValue('')
        })
        setDataSource(newDataSource)
        setSortedInfo({
          order: 'ascend',
          columnKey: 'validatedCode',
        })
        lines.length > 1
          ? message.success('Přidáno ' + lines.length + ' záznamů')
          : message.success('Záznam přidán')
      }
    },
    [dataSource, verifyIco]
  )

  return (
    <>
      <div className="flex space-y-2 lg:space-x-2 lg:space-y-0 flex-wrap lg:flex-nowrap justify-end">
        <div className="w-full" ref={props.inputRef}>
          <Input.TextArea
            placeholder="Zadejte nebo vložte IČ, která chcete zkontrolovat a potvrďte enterem"
            onChange={handleChange}
            value={inputValue}
            maxLength={monitoringInputLimit}
            autoSize
            size="large"
            autoFocus
            className="placeholder:truncate"
          />
        </div>
        <div ref={props.buttonRef}>
          {props.type === MonitoringInputType.CuzkIco && (
            <CuzkMonitorAddSubjectModal dataSource={dataSource} />
          )}
          {props.type === MonitoringInputType.CeeIco && (
            <CeeMonitorAddSubjectModal dataSource={dataSource} />
          )}
        </div>
      </div>
      <Fade show={dataSource.length > 0}>
        <Table
          className="mt-4"
          dataSource={[...dataSource]}
          columns={columns}
          bordered
          rowClassName="animate-fadeIn"
          locale={{
            emptyText: 'Zadejte nebo vložte IČ do textového pole.',
          }}
        />
      </Fade>
    </>
  )
}
